import { useMemo, useState, useEffect } from "react";
import { Howl } from "howler";
import gameBackgroundSoundPath from "../../assets/sounds/bg-music.mp3";
import styles from "./loaderPage.module.css";
import { Loader } from "../../components/svg/loader";
import { CardOne } from "../../components/cards/cardOne";
import { LasersShooting } from "../../components/svg/lasersShooting";
import { useAuth } from "../../context/auth";

const API_BASE_URL = process.env.REACT_APP_SERVER_URL || 'http://localhost:8080';

// Reusable loader wrapper component that accepts children
export const LoaderWrapper = ({ children }) => {
  // Initialize showGamePage based on whether data is already available
  const { setGameData, setGameReady, user: authUser, preferences: authPreferences, hasInitiallyFetched, themeData: authThemeData } = useAuth();

  const [showLoader, setShowLoader] = useState(!hasInitiallyFetched);
  const [showGoButton, setShowGoButton] = useState(false);
  const [showContent, setShowContent] = useState(hasInitiallyFetched);
  const [audioLoaded, setAudioLoaded] = useState(false);
  const [apiLoaded, setApiLoaded] = useState(hasInitiallyFetched);
  const [user, setUser] = useState(authUser);
  const [preferences, setPreferences] = useState(authPreferences);
  // Preserve existing themeData from auth context if already fetched (e.g. second LoaderWrapper on /online-multiplayer)
  const [themeData, setThemeData] = useState(authThemeData);

  // Fetch session first, then /me only if logged in. Items API is never called here — only on store open.
  const fetchAllData = async () => {
    try {
      // Step 1: check session
      const sessionResult = await fetch(`${API_BASE_URL}/api/auth/session-with-preferences`, {
        credentials: 'include'
      })
        .then(async (response) => {
          if (response.ok) {
            const data = await response.json();
            return data.success ? { user: data.user, preferences: data.preferences } : null;
          }
          return null;
        })
        .catch(() => null);

      if (!sessionResult) {
        // Not logged in or session API failed — use defaults
        setUser(null);
        setPreferences(null);
        setThemeData(null);
        return;
      }

      setUser(sessionResult.user);
      setPreferences(sessionResult.preferences);

      // Step 2: logged in — fetch /me for selected theme/board (backend-validated)
      const meResult = await fetch(`${API_BASE_URL}/api/user/me`, {
        credentials: 'include'
      })
        .then(async (response) => {
          if (response.ok) return await response.json();
          return null;
        })
        .catch(() => null);

      // If /me fails, themeData stays null → contexts fall back to Blaze + none board
      setThemeData(meResult);
    } catch (error) {
      setUser(null);
      setPreferences(null);
      setThemeData(null);
    } finally {
      setApiLoaded(true);
    }
  };

  const backgroundSoundTwo = useMemo(() => {
    return new Howl({
      src: [gameBackgroundSoundPath],
      onload: () => {
        setAudioLoaded(true);
      },
    });
  }, []);

  // Load API data on component mount - only if not already loaded
  useEffect(() => {
    // Check if preferences have been initially fetched
    if (hasInitiallyFetched) {
      // Use existing data from auth context (can be null if not logged in)
      setUser(authUser);
      setPreferences(authPreferences);
      setApiLoaded(true);
      // If we already have data, mark game as ready immediately
      setGameReady(true);
    } else {
      // Fetch fresh data if not available
      fetchAllData();
    }
    // Only run on mount - don't re-run when auth state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Show content when both audio and API are loaded
  useEffect(() => {
    if (audioLoaded && apiLoaded) {
      // Set the user data in auth context (including theme data and items)
      setGameData(user, preferences, themeData, null);

      setShowLoader(false);
      setShowContent(true);

      // Mark game as ready - this will trigger theme application
      setGameReady(true);
    }
  }, [audioLoaded, apiLoaded, user, preferences, themeData, setGameData, setGameReady]);

  return (
    <>
      {!showContent ? (
        <CardOne
          customTag="div"
          attributes={{
            className: styles["page"],
          }}
        >
          <LasersShooting></LasersShooting>
          <div className={styles["loader"]}>
            <div className={styles["logo"] + " " + styles["logo-" + "theme-0"]}>
              <b>
                M<span>-</span>tr<span>i</span>s
              </b>
            </div>
            <div className={styles["container"]}>
              {showGoButton ? (
                <button
                  className={styles["lets-go-button"]}
                  onClick={() => {
                    setShowGoButton(false);
                    setShowContent(true);
                  }}
                >
                  Lets Go
                </button>
              ) : (
                <>
                  <Loader></Loader>
                  <div className={styles["loading-text"]}>
                    {!apiLoaded ? (hasInitiallyFetched ? "Loading..." : "Loading session...") : "Downloading assets"}
                  </div>
                </>
              )}
            </div>
          </div>
        </CardOne>
      ) : null}
      {showContent ? (
        // Render children with backgroundSoundTwo prop if it's a function, otherwise just render children
        typeof children === 'function' ? children({ backgroundSoundTwo }) : children
      ) : null}
    </>
  );
};
